import React from "react";
import Nav from "./Nav";
import { Users, Target, Lightbulb, Heart, Instagram, Facebook, Twitter, Mail } from "lucide-react";
import { motion } from "framer-motion";
import image1 from "../temp_assets/about.png";

const AboutUs = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-pink-100 to-pink-200">
      <Nav />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-1 mb-4 text-sm font-medium text-pink-600 bg-pink-50 rounded-full">About Us</span>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Our Story</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Dedicated to redefining fashion through innovation and excellence since 2024
            </p>
          </motion.div>
          {/* Main Content */}
          <motion.div 
            className="grid md:grid-cols-2 gap-12 mb-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-[400px]">
                <img 
                  src={image1}
                  alt="Our Team"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-[250px]">
                <img
                  src={image1}
                  alt="Our Office"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
            <div className="flex flex-col justify-center space-y-8">
              <motion.div {...fadeInUp}>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Who We Are
                </h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Welcome to Karishma's Closet, where fashion meets innovation. We're more than just a clothing store—we're a destination for self-expression and style discovery. Our carefully curated collections reflect our commitment to quality, sustainability, and inclusive fashion that celebrates every individual.
                </p>
              </motion.div>
              <motion.div 
                className="grid grid-cols-2 gap-6"
                {...fadeInUp}
              >
                <div className="bg-white p-6 rounded-xl shadow-sm border border-pink-100">
                  <h3 className="font-semibold text-gray-900 mb-2">1M+</h3>
                  <p className="text-gray-600">Happy Customers</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-pink-100">
                  <h3 className="font-semibold text-gray-900 mb-2">50+</h3>
                  <p className="text-gray-600">Countries Served</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
          {/* Values Section */}
          <motion.div 
            className="grid md:grid-cols-3 gap-8 mb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-pink-100 hover:shadow-lg transition-shadow duration-300">
              <div className="bg-pink-50 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Our Mission
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Bringing affordable, high-quality fashion to everyone</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Promoting inclusivity through diverse sizing and styles</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Championing sustainable and ethical fashion practices</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-pink-100 hover:shadow-lg transition-shadow duration-300">
              <div className="bg-pink-50 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                <Lightbulb className="w-6 h-6 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                What We Offer
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Curated women's fashion for every occasion</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Sophisticated men's collection</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Trendy and comfortable kids' wear</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-pink-100 hover:shadow-lg transition-shadow duration-300">
              <div className="bg-pink-50 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                <Heart className="w-6 h-6 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Why Choose Us?
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Premium quality materials and craftsmanship</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Fast, reliable worldwide shipping</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Dedicated customer support team</span>
                </li>
              </ul>
            </div>
          </motion.div>
          {/* Community Section */}
          <motion.div 
            className="bg-white rounded-2xl shadow-sm border border-pink-100 p-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex p-3 mb-8 bg-pink-50 rounded-full">
                <Users className="w-8 h-8 text-pink-500" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Join Our Community
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Become part of our growing fashion community. Share your style, get inspired, and stay updated with the latest trends and exclusive offers.
              </p>
              <div className="flex justify-center space-x-6">
                <a href="#" className="p-3 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors duration-300">
                  <Instagram className="w-6 h-6 text-pink-500" />
                </a>
                <a href="#" className="p-3 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors duration-300">
                  <Facebook className="w-6 h-6 text-pink-500" />
                </a>
                <a href="#" className="p-3 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors duration-300">
                  <Twitter className="w-6 h-6 text-pink-500" />
                </a>
                <a href="mailto:support@karishmas-closet.com" className="p-3 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors duration-300">
                  <Mail className="w-6 h-6 text-pink-500" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
export default AboutUs;